import { z } from 'zod';
import { uuidV7Schema } from '../uuid.js';
/**
 * M11 — le **schéma du réseau** que l'agent dessine sur la tablette, rangé en JSON dans le
 * relevé (« personne ne requête l'intérieur d'un dessin ») et rendu en image pour que
 * l'archive reste lisible dans dix ans.
 *
 * ## Ce fichier est le contrat, pas l'éditeur
 *
 * L'app terrain dessine avec ses propres éléments SVG : **aucune bibliothèque de canvas** —
 * ni tldraw ni Excalidraw, ~1 Mo de SDK précaché par le Service Worker pour six formes et
 * deux types de traits. Mais ce choix doit rester **réversible sans migration de données**,
 * et c'est ce modèle qui le garantit : il ne connaît ni React, ni le DOM, ni le format
 * interne d'un outil tiers. Le jour où une bibliothèque s'impose, on écrit un adaptateur
 * « notre JSON ↔ le leur » — on ne retouche pas les tablettes déjà en tournée.
 *
 * D'où trois règles qui ne se devinent pas en lisant la forme :
 *
 * 1. **Le vocabulaire est celui du métier**, pas celui d'un moteur de dessin : `manhole`
 *    (Regard), `wastewaterPipe` (Conduite EU)… La liste est celle de `HANDOFF-ANOMALIES.md`,
 *    et elle est fermée par le métier, pas par l'outil.
 * 2. **La couleur et l'épaisseur sont des jetons**, jamais un `#0f766e` ni un nombre de
 *    pixels. Un hexadécimal rangé dans dix mille dessins est une migration le jour où la
 *    charte bouge ; un jeton se re-résout à l'affichage.
 * 3. **La géométrie est une liste de points**, la même pour toutes les formes. Un élément
 *    posé en a un (son centre), une boîte deux (deux coins opposés), une conduite ou un
 *    tracé à main levée autant qu'il en faut. Un adaptateur vers n'importe quel moteur se
 *    réduit alors à une projection, jamais à une conversion de structure.
 */
/**
 * Les **éléments réseau** : les six de la maquette validée — Regard · Tabouret · Siphon ·
 * Fosse · Conduite EU · Conduite EP —, puis ceux que le catalogue de GHP DIGIT y ajoute.
 *
 * ⚠️ Cette liste **ne s'étend pas depuis un écran** : un élément de plus est une décision
 * métier, pas une option d'éditeur. La preuve métier au-delà des six est le catalogue de
 * GHP DIGIT, repris par l'arbitrage du 24/09/2026 (#318).
 *
 * - `publicNetwork` — le **réseau public**. Son réseau, EU, EP ou unitaire, est son
 *   trait ({@link SCHEMATIC_NETWORKS}) et non son `kind` : un réseau typé plutôt que
 *   trois éléments doublés. Les deux conduites privées gardent leur réseau dans le `kind`,
 *   parce que des dessins déjà transmis l'y portent.
 * - Les ouvrages de GHP DIGIT (rapport 02) : tampon, regard à grille, bonde de sol, poste de
 *   relevage — typés eux aussi par leur trait —, gouttière, bouchon, siphons horizontal et
 *   vertical, caniveau à grille.
 * - `downpipeOutfall` — la **gouttière sur sol**, la « Gouttière Écoulement » de GHP DIGIT, que
 *   rien n'y posait faute de direction (#581). Deux points : la gouttière, puis la pointe de la
 *   flèche du rejet, dans le sens où l'eau part.
 */
export const NETWORK_ELEMENT_KINDS = [
    'manhole',
    'inspectionChamber',
    'trap',
    'septicTank',
    'wastewaterPipe',
    'stormwaterPipe',
    'publicNetwork',
    'manholeCover',
    'gratedInlet',
    'floorDrain',
    'liftingStation',
    'downpipe',
    'plug',
    'trapHorizontal',
    'trapVertical',
    'gratedChannel',
    'downpipeOutfall',
];
/**
 * Les formes libres des OUTILS de la maquette — ce que l'agent dessine **autour** du réseau :
 * le contour de l'habitation, une flèche vers le collecteur, une annotation.
 *
 * `pan` et `select` n'y sont pas : ce sont des gestes, pas des formes. Ils ne produisent
 * aucun nœud, donc ils n'ont rien à faire dans le document.
 *
 * `polygon` est la « Maison » de GHP DIGIT : un contour **fermé**, posé d'angle en angle. Le
 * dernier point ne répète pas le premier — la fermeture est dans le `kind`, pas dans la donnée.
 * `shelter` est le « Préau » : la même ligne d'angles, ouverte. `roomLabel` et `outletLabel`
 * sont les étiquettes posées d'un appui — une pièce ou un appareil (« wc »), qui porte son étage,
 * et un exutoire ou un constat (« Puisard »), qui n'en porte pas.
 */
export const SCHEMATIC_SHAPE_KINDS = [
    'rectangle',
    'ellipse',
    'line',
    'arrow',
    'freehand',
    'text',
    'polygon',
    'shelter',
    'roomLabel',
    'outletLabel',
];
export const SCHEMATIC_NODE_KINDS = [...NETWORK_ELEMENT_KINDS, ...SCHEMATIC_SHAPE_KINDS];
/**
 * Les couleurs de trait, **nommées par ce qu'elles désignent** et non par ce qu'elles valent :
 * l'encre du bâti, les eaux usées, les eaux pluviales, l'alerte — les quatre de la maquette —,
 * et le réseau unitaire, vert tiret-point dans GHP DIGIT.
 *
 * ⚠️ C'est le point qui rend un dessin de 2026 relisible en 2036. Ranger `#0d5c63` dans le
 * JSON collerait la charte graphique du jour à une donnée de terrain ; le jeton, lui, se
 * re-résout à l'affichage (`packages/ui`), et la maquette peut changer de teinte sans qu'une
 * seule tablette ait à être migrée.
 */
export const SCHEMATIC_STROKES = ['ink', 'wastewater', 'stormwater', 'alert', 'combined'];
/**
 * Les traits qui **nomment un réseau** : c'est par eux qu'un ouvrage ou le réseau public se
 * dit EU, EP ou unitaire. Un sous-ensemble de {@link SCHEMATIC_STROKES}, pas un champ de plus :
 * le trait d'un élément réseau est déjà ce que le bureau lit pour reconnaître son réseau.
 */
export const SCHEMATIC_NETWORKS = ['wastewater', 'stormwater', 'combined'];
/** Les trois épaisseurs de la maquette — des jetons, pour la raison de {@link SCHEMATIC_STROKES}. */
export const SCHEMATIC_STROKE_WIDTHS = ['thin', 'medium', 'thick'];
/**
 * Un point du plan de dessin, en **unités de document** : ni pixels d'écran, ni mètres. Le
 * zoom et la taille de la tablette n'entrent donc jamais dans la donnée archivée — deux
 * agents sur deux tablettes différentes produisent le même dessin.
 */
export const schematicPointSchema = z.looseObject({
    x: z.number().finite(),
    y: z.number().finite(),
});
/**
 * Une forme du dessin.
 *
 * ⚠️ **`kind` est une chaîne libre, pas une énumération**, et c'est délibéré (M21) : une
 * tablette déployée avant l'API peut poser un élément que celle-ci ne nomme pas encore, et
 * refuser ferait tomber le relevé **entier** — un dessin de terrain ne se reconstitue pas.
 * L'écran, lui, ne propose que {@link SCHEMATIC_NODE_KINDS} ; ce qu'il ne connaît pas se
 * dessine en forme générique plutôt que de disparaître. Même raison pour `stroke` et
 * `strokeWidth`.
 *
 * ⚠️ **`points` est le seul refus de ce modèle** : un nœud sans géométrie ne se dessine nulle
 * part et ne se répare pas. C'est de l'intégrité structurelle, la première des trois raisons
 * que le garde-fou 3 laisse ouvertes.
 *
 * ⚠️ Et **le refus s'arrête là : il n'y a pas de `.max` sur `points`.** Un tracé plus long que
 * {@link MAX_SCHEMATIC_NODE_POINTS} est gardé et signalé
 * ({@link hasTruncatedSchematicStroke}), jamais rejeté — le plafond est celui de l'écran qui
 * pose les points, pas celui du modèle qui les relit. Refuser ici ferait tomber le relevé
 * **entier**, et un dessin de terrain ne se reconstitue pas (M21, garde-fou 3).
 */
export const schematicNodeSchema = z.looseObject({
    /** Une uuid v7 tirée par la tablette (M7) : c'est elle qui rend le rejeu idempotent. */
    id: uuidV7Schema,
    kind: z.string().min(1),
    points: z.array(schematicPointSchema).min(1),
    /** Le libellé d'un nœud `text`, et l'étiquette d'une conduite (« EU », « épandage »). */
    text: z.string().nullish(),
    stroke: z.string().min(1),
    strokeWidth: z.string().min(1),
    /**
     * Les ouvrages auxquels une conduite est **accrochée**, amont puis aval : l'`id` d'un autre
     * nœud. C'est ce qui la fait suivre l'ouvrage qu'on déplace.
     *
     * ⚠️ Un indice pour l'éditeur, jamais la géométrie : `points` reste la seule source du tracé,
     * et un lecteur qui ignore ces champs dessine le même plan. Un `id` qui ne désigne plus rien
     * — l'ouvrage a été supprimé — ne se refuse donc pas : la conduite est simplement détachée.
     */
    from: z.string().nullish(),
    to: z.string().nullish(),
    /**
     * L'étage d'une étiquette de pièce : 2, 1, 0 ou −1, le sous-sol. Un nombre et non un texte,
     * pour que « wc (−1) » s'écrive à l'affichage et reste relisible si la présentation change.
     */
    level: z.number().int().nullish(),
    /**
     * La taille d'un symbole posé d'un appui, en multiple de sa taille d'origine. Absente, elle
     * vaut 1. ⚠️ Pas de borne ici : l'écran la tient, et une valeur aberrante se ramène au rendu
     * plutôt que de faire tomber le relevé (M21).
     */
    scale: z.number().nullish(),
});
/**
 * Ce qu'un **écran** laisse poser de formes. Un schéma de branchement chargé en compte une
 * trentaine ; ce plafond est vingt fois au-dessus.
 *
 * ⚠️ **Ce n'est pas un refus du modèle, et ça ne doit pas le redevenir.** Il a été écrit en
 * `.max` sur `nodes` trente lignes plus bas, contre la doctrine que {@link schematicNodeSchema}
 * énonce juste au-dessus : un dessin de 601 formes faisait tomber le `survey` **entier**, donc
 * un `400` au dépôt, que la file classe en échec définitif — le contrôle restait verrouillé et
 * cessait d'être transmissible, sans qu'aucun écran ne nomme le dessin. On borne l'écran qui
 * pose, on signale à celui qui transmet ({@link isSchematicPlanFull}), on ne refuse pas à la
 * relecture.
 *
 * ⚠️ Il borne un axe que personne ne pousse : chaque forme demande un geste, et un agent n'en
 * fait pas six cents. Ce qui grossit sans qu'on s'en aperçoive est
 * {@link MAX_SCHEMATIC_NODE_POINTS} — l'autre axe.
 */
export const MAX_SCHEMATIC_NODES = 600;
/**
 * Ce qu'une **seule forme** porte au grand maximum de points.
 *
 * ⚠️ C'est ici qu'est l'amplification, et le commentaire de {@link MAX_SCHEMATIC_NODES} la
 * plaçait sur le mauvais axe : un tracé au crayon ne produit **qu'un nœud**, dont le tableau
 * de points s'allonge à chaque événement de déplacement. À 120 Hz et 22 octets par point
 * arrondi, une minute de crayon écrit plus de 150 Kio dans le corps de la proposition, et le
 * dépôt est plafonné à 1 Mo. Le `413` qui suivrait tombe avant le contrôleur et la file le
 * classe en échec **définitif** : l'agent doit rouvrir la fiche et alléger le dessin (#246).
 *
 * 2000 points, c'est une quinzaine de secondes de tracé continu après filtrage des points
 * quasi immobiles — largement au-delà de ce qu'un contour d'habitation demande.
 */
export const MAX_SCHEMATIC_NODE_POINTS = 2000;
/**
 * La version de **cette forme de document**. Distincte de `SURVEY_VERSION` : le relevé et le
 * dessin qu'il contient n'évoluent pas au même rythme, et c'est ce numéro qu'un futur
 * adaptateur vers une bibliothèque tierce lira pour savoir ce qu'il a sous la main.
 */
export const NETWORK_SCHEMATIC_VERSION = 1;
/**
 * Le document complet.
 *
 * ⚠️ `version` est un **entier**, jamais un `z.literal` : ce dessin est imbriqué dans le
 * relevé, donc un refus ici ferait tomber la fiche entière — une tablette d'après la
 * prochaine version détruirait son propre relevé à la relecture. On garde, on signale, le
 * back-office tranche (M16, M21).
 *
 * ⚠️ **`nodes` n'a pas de `.max`**, pour exactement la même raison que `points` : ce schéma
 * relit un dessin, il ne le saisit pas. Le compte de formes est borné par l'écran et signalé à
 * la transmission ({@link MAX_SCHEMATIC_NODES}).
 */
export const networkSchematicSchema = z.looseObject({
    version: z.number().int().min(1),
    nodes: z.array(schematicNodeSchema),
});
/**
 * Le dessin d'un contrôle qui n'en a pas encore.
 *
 * Un document vide plutôt qu'`undefined` : sans lui, chaque lecteur — éditeur, export,
 * back-office — aurait à traiter l'absence, et l'un d'eux finirait par l'oublier.
 */
export function emptyNetworkSchematic() {
    return { version: NETWORK_SCHEMATIC_VERSION, nodes: [] };
}
/**
 * Un tracé a-t-il atteint le plafond de points ?
 *
 * C'est la moitié « avertir » du plafond : l'écran cesse d'allonger le tracé pour garder le
 * dépôt sous sa limite, mais la **fin du geste est alors perdue**, et personne ne le devine en
 * regardant le dessin. Dérivé, jamais stocké (M21) — une liste d'avertissements enregistrée
 * serait à resynchroniser à chaque retouche du brouillon.
 */
export function hasTruncatedSchematicStroke(schematic) {
    return schematic.nodes.some((node) => node.points.length >= MAX_SCHEMATIC_NODE_POINTS);
}
/**
 * Le plan a-t-il atteint son nombre maximal de formes ?
 *
 * Le pendant de {@link hasTruncatedSchematicStroke} sur l'autre axe : l'écran cesse de poser,
 * et rien dans le dessin ne montre qu'un geste n'a pas abouti. Dérivé, jamais stocké (M21).
 */
export function isSchematicPlanFull(schematic) {
    return schematic.nodes.length >= MAX_SCHEMATIC_NODES;
}
//# sourceMappingURL=network-schematic.js.map